import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { readPage } from '../../../utils/apiResult';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';
import { formatRate } from '../../ats-requisitions/recruitingCost';

// `expired` DEPODA BİR DURUM DEĞİLDİR: arka uç, yayındaki ama son başvuru tarihi
// geçmiş satırlar için onu TÜRETİR (AtsJobPostingDataAccess.ExpiredStatus) —
// kariyer sitesinin gizleme kuralıyla aynı yüklem. Süzgeç ve durum etiketi bu
// yüzden onu da tanır; durum geçişleri ise depodaki `published`'a göre çalışır.
export const POSTING_STATUSES = ['draft', 'published', 'expired', 'paused', 'closed', 'archived'];
export const POSTING_CHANNELS = [
  'linkedin', 'indeed', 'kariyernet', 'yenibiris',
  'career_site', 'referral', 'external_agency',
];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const useAtsJobPostingsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/ats-job-postings?${new URLSearchParams(paramsToUse)}`)
      // api.js interceptor'ı OKUMALARDA ASLA reject ETMEZ; gerçek sinyal
      // res.data.status. res.data?.data doğrudan okunursa 403/500/ağ hatası
      // "kayıt yok" diye çizilirdi (utils/apiResult).
      .then((res) => {
        const { ok, items, pagination: pg } = readPage(res);
        setLoadFailed(!ok);
        setData(items);
        setPagination(pg);
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  // BE wants a CSV channels string; the form keeps an array. The drawer also
  // emits the date as ISO; null when blank.
  const buildPayload = (formData) => ({
    requisitionId: formData.requisitionId,
    coordinatorUserId: formData.coordinatorUserId || null,
    title: formData.title,
    slug: formData.slug || null,
    descriptionMarkdown: formData.descriptionMarkdown || '',
    responsibilitiesMarkdown: formData.responsibilitiesMarkdown || null,
    requirementsMarkdown: formData.requirementsMarkdown || null,
    benefitsMarkdown: formData.benefitsMarkdown || null,
    expiresAt: formData.expiresAt || null,
    channelsCsv: Array.isArray(formData.channels)
      ? formData.channels.filter(Boolean).join(',')
      : (formData.channels || null),
  });

  const create = useCallback(async (formData) => {
    try {
      await api.post('/v1/ats-job-postings', buildPayload(formData));
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const update = useCallback(async (id, formData) => {
    try {
      await api.put(`/v1/ats-job-postings/${id}`, buildPayload(formData));
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const changeStatus = useCallback(async (id, action) => {
    try {
      await api.post(`/v1/ats-job-postings/${id}/status`, { action });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const remove = useCallback(async (id) => {
    try {
      await api.delete(`/v1/ats-job-postings/${id}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const columns = useMemo(() => {
    const statusLabel = (s) => t(`ats.jobPosting.status.${s}`, { defaultValue: s });
    const channelLabel = (c) => t(`ats.jobPosting.channel.${c}`, { defaultValue: c });

    // Sıralama: backend AtsJobPostingDataAccess orderBy switch'i title | status | postedat |
    // applicationcount — `sortKey` bunlara eşler; diğer kolonlar `sort: false`.
    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'title',
        label: t('ats.jobPosting.title'),
        options: {
          filter: true,
          sort: true,
          sortKey: 'title',
          filterType: 'textField',
          filterOptions: { defaultOp: 'like' },
        },
      },
      {
        // FE-18 + BP-24 — ilan hangi kadroya ait? Arka uç requisitionTitle'ı artık
        // pozisyonun ünvanından projelendiriyor (talebin kendi Title'ı ÜRETİMDE hep
        // boştur). Kod varsa "[SAT-01] Satış Uzmanı" etiketi kurulur.
        name: 'requisitionTitle',
        label: t('ats.jobPosting.requisitionTitleColumn'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (v, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const code = row?.positionCode ? '[' + row.positionCode + '] ' : '';
            return (code + (v || '')).trim() || '-';
          },
        },
      },
      {
        // FE-18 — koordinatör adı (arka uçta iki id uzayı da çözülür).
        name: 'coordinatorName',
        label: t('ats.jobPosting.coordinatorColumn'),
        options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
      },
      {
        name: 'slug',
        label: t('ats.jobPosting.slug'),
        options: {
          filter: true,
          sort: false,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like' },
        },
      },
      {
        name: 'status',
        label: t('ats.jobPosting.statusLabel'),
        options: {
          ...multiSelectFilter(t('ats.jobPosting.statusLabel'), POSTING_STATUSES, statusLabel),
          sortKey: 'status',
          customBodyRender: (v) => statusLabel(v),
        },
      },
      {
        name: 'channels',
        label: t('ats.jobPosting.channels'),
        options: {
          ...multiSelectFilter(t('ats.jobPosting.channels'), POSTING_CHANNELS, channelLabel),
          sort: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            try {
              const arr = JSON.parse(row?.channelsJson || '[]');
              return arr.map(channelLabel).join(', ') || '-';
            } catch { return '-'; }
          },
        },
      },
      dateFilterColumn('postedAt', t('ats.jobPosting.postedAt'), lang, { sortKey: 'postedat' }),
      dateFilterColumn('expiresAt', t('ats.jobPosting.expiresAt'), lang, { sort: false }),
      // SPEC F3 — `viewCount` artık GERÇEK: kariyer sitesindeki ilan detayının
      // günlük görüntülenme tablosundan toplam (eski ölü kolon değil; süzgeç de
      // sunucuda o toplamdan okur). Tekil görüntülenme / dönüşüm yalnız gösterim.
      numericFilterColumn('viewCount', t('ats.jobPosting.viewCount'), { decimals: 0, sort: false }),
      {
        name: 'uniqueViews',
        label: t('ats.jobPosting.uniqueViews'),
        options: {
          filter: false, sort: false,
          customBodyRender: (v) => (v === null || v === undefined ? '—' : Number(v)),
        },
      },
      numericFilterColumn('applicationCount', t('ats.jobPosting.applicationCount'), { decimals: 0, sortKey: 'applicationcount' }),
      {
        // başvuru / tekil görüntülenme; payda 0 → null → "—" (%0 DEĞİL).
        name: 'conversionRate',
        label: t('ats.jobPosting.conversionRate'),
        options: {
          filter: false, sort: false,
          customBodyRender: (v) => formatRate(v, lang),
        },
      },
    ];
  }, [t, lang, data]);

  // Static option set surfaced via the asyncSelect isMulti widget so the
  // channels field plays nicely with the existing form drawer.
  const channelOptions = useMemo(
    () => POSTING_CHANNELS.map((c) => ({ id: c, name: t(`ats.jobPosting.channel.${c}`, { defaultValue: c }) })),
    [t],
  );

  // Helpers for the requisition asyncSelect — the recruiter picks an APPROVED
  // requisition; the picker label includes the position code + title so the
  // selection is unambiguous when several requisitions share the same role.
  const requisitionLabel = (r) => {
    if (!r) return '';
    const code = r.positionCode ? `[${r.positionCode}] ` : '';
    const title = r.positionTitleName || '';
    return (code + title).trim() || r.id;
  };

  // Fetch a single approved requisition by id — used when the page is opened
  // from a "İlana Çevir" deep-link with createFromRequisition=<id> in the URL.
  const fetchRequisitionById = useCallback(async (id) => {
    try {
      const res = await api.get(`/v1/ats-requisitions/${id}`);
      return res.data?.data || null;
    } catch {
      return null;
    }
  }, []);

  const objectSchema = useMemo(() => [
    {
      name: 'requisitionId', label: t('ats.jobPosting.requisitionId'),
      type: 'asyncSelect', grid: 12, required: true,
      description: t('ats.jobPosting.requisitionIdHint'),
      // Only approved requisitions are valid posting targets — BE supports
      // status[in] filter, list endpoint already handles it.
      onSearch: async (q) => {
        const params = { 'status[in]': 'approved', pageSize: 30 };
        if (q) params['position[like]'] = q;
        const res = await api.get('/v1/ats-requisitions', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((it) => ({ id: it.id, name: requisitionLabel(it) }));
      },
      onGetById: async (id) => {
        const r = await fetchRequisitionById(id);
        return r ? { id, name: requisitionLabel(r) } : { id, name: '' };
      },
    },
    {
      name: 'coordinatorUserId', label: t('ats.jobPosting.coordinatorUserId'),
      type: 'asyncSelect', grid: 12,
      description: t('ats.jobPosting.coordinatorUserIdHint'),
      onSearch: async (q) => {
        const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((e) => ({ id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim() || e.fullName || e.corporateEmail || e.id, profilePicture: e.profilePicture }));
      },
      onGetById: async (id) => {
        try {
          const res = await api.get(`/v1/employees/${id}`);
          const e = res.data?.data || {};
          return { id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim() || e.fullName || e.corporateEmail || id, profilePicture: e.profilePicture };
        } catch { return { id, name: '', profilePicture: null }; }
      },
    },
    { name: 'title', label: t('ats.jobPosting.title'), type: 'text', grid: 12, required: true },
    { name: 'slug', label: t('ats.jobPosting.slug'), type: 'text', grid: 12 },
    { name: 'descriptionMarkdown', label: t('ats.jobPosting.descriptionMarkdown'), type: 'richText', grid: 12, required: true, rows: 8 },
    { name: 'responsibilitiesMarkdown', label: t('ats.jobPosting.responsibilitiesMarkdown'), type: 'richText', grid: 12, rows: 6 },
    { name: 'requirementsMarkdown', label: t('ats.jobPosting.requirementsMarkdown'), type: 'richText', grid: 12, rows: 6 },
    { name: 'benefitsMarkdown', label: t('ats.jobPosting.benefitsMarkdown'), type: 'richText', grid: 12, rows: 6 },
    { name: 'expiresAt', label: t('ats.jobPosting.expiresAt'), type: 'date', grid: 6 },
    {
      name: 'channels', label: t('ats.jobPosting.channels'),
      type: 'asyncSelect', grid: 12, isMulti: true,
      onSearch: async () => channelOptions,
      onGetById: async (id) => channelOptions.find((c) => c.id === id) || { id, name: id },
    },
  ], [t, channelOptions, fetchRequisitionById]);

  return {
    fetching, data, loadFailed, pagination, columns, objectSchema,
    refresh, create, update, changeStatus, remove,
    fetchRequisitionById,
  };
};

export default useAtsJobPostingsApi;
